/**
 * EditableField - Inline editable field with hover actions
 * Shows edit/restore icons on hover, compact design like ShortLinkItem
 */

import React from 'react';
import { HStack, Input, IconButton, Spinner, Tooltip } from '@chakra-ui/react';
import { Pencil, Check, X, RotateCcw } from 'lucide-react';
import { useEditableField, UseEditableFieldOptions } from '../../../../../hooks/useEditableField';
import './EditableField.css';

export interface EditableFieldProps extends Omit<UseEditableFieldOptions, 'chartId'> {
  /** Chart ID this field belongs to */
  chartId: string;
  /** Label text */
  label: string;
  /** Disable editing (e.g., in share mode) */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
  /** Show only action buttons (edit/restore), hide label and value */
  actionsOnly?: boolean;
  /** Callback when editing state changes */
  onEditingChange?: (isEditing: boolean) => void;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  chartId,
  label,
  disabled = false,
  className = '',
  actionsOnly = false,
  onEditingChange,
  ...hookOptions
}) => {
  const {
    value,
    isEditing,
    editValue,
    hasBackup,
    originalValue,
    startEdit,
    setEditValue,
    save,
    cancel,
    restore,
    isLoading
  } = useEditableField({ chartId, ...hookOptions });

  // Notify parent when editing state changes
  React.useEffect(() => {
    onEditingChange?.(isEditing);
  }, [isEditing, onEditingChange]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  // Don't render if value is empty placeholder (unless actionsOnly mode)
  if (!actionsOnly && (value === '-' || (!value && !isEditing))) {
    return null;
  }

  // Actions-only mode: show just edit/restore buttons inline
  if (actionsOnly) {
    if (disabled) return null;

    return isEditing ? (
      <HStack spacing={1} className="editable-field-input-group">
        <Input
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          size="xs"
          variant="flushed"
          autoFocus
          isDisabled={isLoading}
          className="editable-field-input"
          width="auto"
          minW="150px"
        />
        <IconButton
          aria-label="Save"
          icon={isLoading ? <Spinner size="xs" /> : <Check size={12} />}
          size="xs"
          variant="ghost"
          colorScheme="green"
          onClick={save}
          isDisabled={isLoading}
        />
        <IconButton
          aria-label="Cancel"
          icon={<X size={12} />}
          size="xs"
          variant="ghost"
          colorScheme="red"
          onClick={cancel}
          isDisabled={isLoading}
        />
      </HStack>
    ) : (
      <HStack spacing={0} className="editable-field-actions">
        <IconButton
          aria-label="Edit"
          icon={<Pencil size={12} />}
          size="xs"
          variant="ghost"
          onClick={startEdit}
        />
        {hasBackup && (
          <Tooltip
            label={`Restore: ${originalValue}`}
            placement="top"
            hasArrow
            fontSize="xs"
          >
            <IconButton
              aria-label={`Restore original: ${originalValue}`}
              icon={isLoading ? <Spinner size="xs" /> : <RotateCcw size={12} />}
              size="xs"
              variant="ghost"
              colorScheme="orange"
              onClick={restore}
              isDisabled={isLoading}
            />
          </Tooltip>
        )}
      </HStack>
    );
  }

  return (
    <div className={`editable-field ${className}`}>
      <span className="editable-field-label">{label}:</span>

      {isEditing ? (
        <HStack spacing={1} flex={1} className="editable-field-input-group">
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            size="xs"
            variant="flushed"
            autoFocus
            isDisabled={isLoading}
            className="editable-field-input"
          />
          <IconButton
            aria-label="Save"
            icon={isLoading ? <Spinner size="xs" /> : <Check size={12} />}
            size="xs"
            variant="ghost"
            colorScheme="green"
            onClick={save}
            isDisabled={isLoading}
          />
          <IconButton
            aria-label="Cancel"
            icon={<X size={12} />}
            size="xs"
            variant="ghost"
            colorScheme="red"
            onClick={cancel}
            isDisabled={isLoading}
          />
        </HStack>
      ) : (
        <div className="editable-field-content">
          <span className="editable-field-value">{value}</span>
          {!disabled && (
            <HStack spacing={0} className="editable-field-actions">
              <IconButton
                aria-label="Edit"
                icon={<Pencil size={12} />}
                size="xs"
                variant="ghost"
                onClick={startEdit}
              />
              {hasBackup && (
                <Tooltip
                  label={`Restore: ${originalValue}`}
                  placement="top"
                  hasArrow
                  fontSize="xs"
                >
                  <IconButton
                    aria-label={`Restore original: ${originalValue}`}
                    icon={isLoading ? <Spinner size="xs" /> : <RotateCcw size={12} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="orange"
                    onClick={restore}
                    isDisabled={isLoading}
                  />
                </Tooltip>
              )}
            </HStack>
          )}
        </div>
      )}
    </div>
  );
};

export default EditableField;
